import { useCallback, useEffect, useState } from 'react';
import socket from '@/client/socket';

const useSocket = () => {
  const [isConnected, setIsConnected] = useState(socket.connected);

  useEffect(() => {
    // The socket is a module-level singleton with autoConnect, so by the time
    // this hook mounts the connect event may have already fired. Seed from
    // the current socket state — the duration log becomes a lower bound for
    // pre-mount sessions, but that's still useful (and "0ms" was misleading).
    let connectedAt = socket.connected ? Date.now() : 0;

    const handleConnect = () => {
      connectedAt = Date.now();
      console.log(
        `[socket] connected id=${socket.id} at=${new Date(connectedAt).toISOString()}`,
      );
      setIsConnected(true);
    };

    const handleDisconnect = (reason: string) => {
      const durationMs = connectedAt ? Date.now() - connectedAt : 0;
      // Reason vocabulary (from socket.io-client docs):
      //   io server disconnect      → server called socket.disconnect()
      //   io client disconnect      → app called socket.disconnect()
      //   ping timeout              → no pong within pingInterval+pingTimeout
      //   transport close           → underlying transport (WS or HTTP) was closed
      //                                (proxy idle, server restart, tab close)
      //   transport error           → low-level network error
      // Pair this log with backend "Client disconnected" lines (TCX-539) to
      // tell apart proxy timeouts, deploys, and browser-initiated closes.
      console.log(
        `[socket] disconnected reason=${reason} duration=${durationMs}ms ` +
          `at=${new Date().toISOString()} document.hidden=${document.hidden}`,
      );
      setIsConnected(false);
    };

    const handleError = (error: Error) => {
      console.error(`[socket] connect_error: ${error.message}`);
    };

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);
    socket.on('connect_error', handleError);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off('connect_error', handleError);
    };
  }, []);

  const onEvent = useCallback(
    (event: string, callback: (...args: unknown[]) => void) => {
      socket.on(event, callback);
      return () => socket.off(event, callback);
    },
    [],
  );

  const emitEvent = useCallback(
    (
      event: string,
      data?: unknown,
      callback?: (...args: unknown[]) => void,
    ) => {
      socket.emit(event, data, callback);
    },
    [],
  );

  return { isConnected, onEvent, emitEvent };
};

export default useSocket;
